/**
 * Division 296 fund earnings builder — SMSF annual return worksheet (v1).
 *
 * Standard SMSF formula (no NALI, PST, or CGT adjustments):
 *   fund earnings = taxable income − assessable contributions + net ECPI
 * Negative raw earnings are floored to $0 for Division 296 purposes.
 */

/**
 * @param {object} [input]
 * @param {number} [input.taxableIncome] - Fund taxable income or loss for the FY ($)
 * @param {number} [input.assessableContributions] - Contributions included in assessable income ($)
 * @param {number} [input.grossEcpi] - Gross exempt current pension income ($)
 * @param {number} [input.ecpiNonDeductibleExpenses] - Expenses denied because they relate to ECPI ($)
 * @param {string|null} [input.financialYear] - e.g. '2026-27'
 */
export function calculateDivision296FundEarnings(input = {}) {
    const {
        taxableIncome = 0,
        assessableContributions = 0,
        grossEcpi = 0,
        ecpiNonDeductibleExpenses = 0,
        financialYear = null,
    } = input;

    const ti = Number(taxableIncome) || 0;
    const ac = Number(assessableContributions) || 0;
    const ge = Number(grossEcpi) || 0;
    const en = Number(ecpiNonDeductibleExpenses) || 0;

    const netEcpi = ge - en;
    const rawEarnings = ti - ac + netEcpi;
    const earnings = Math.max(0, rawEarnings);
    const flooredFromNegative = rawEarnings < 0;

    const warnings = [];
    if (flooredFromNegative) {
        warnings.push(
            'Raw fund earnings were negative; this worksheet treats Division 296 earnings as nil for the year. Loss carry-forward rules are subject to final regulations and ATO guidance and are not modelled here yet.'
        );
    }
    if (ge > 0 && en === 0) {
        warnings.push(
            'If expenses were denied because they relate to exempt pension income, enter them under ECPI non-deductible expenses.'
        );
    }
    warnings.push(
        'Member-level earnings attribution (how fund earnings are allocated to you) is provisional pending final regulations and ATO guidance. Prefer reported Division 296 fund earnings when you have them.'
    );
    warnings.push('Non-concessional contributions are not deducted - they are excluded from assessable income.');
    warnings.push('NALI, PST, and CGT adjustments are not included in this worksheet (v1).');
    warnings.push(
        'SMSF cost-base grandfathering elections are available under the Act for small funds; they are not auto-applied here. Confirm election timing with your adviser or accountant.'
    );

    return {
        earnings,
        rawEarnings,
        netEcpi,
        breakdown: {
            taxableIncome: ti,
            assessableContributions: ac,
            grossEcpi: ge,
            ecpiNonDeductibleExpenses: en,
            netEcpi,
            rawEarnings,
            earnings,
        },
        warnings,
        flooredFromNegative,
        financialYear,
        earningsSource: 'fund_builder',
    };
}

/**
 * Pre-fill assessable contributions from SMSF state (concessional used only).
 * @param {object|null} smsfState
 * @returns {number}
 */
export function deriveAssessableContributionsFromSmsfState(smsfState) {
    if (!smsfState) return 0;

    const fromContrib = smsfState.contributions?.concessionalUsed
        ?? smsfState.smsfConcessionalUsed;
    if (fromContrib != null && fromContrib !== '') {
        return Number(fromContrib) || 0;
    }

    const members = smsfState.smsfData?.members ?? smsfState.members ?? [];
    if (!Array.isArray(members) || members.length === 0) return 0;

    return members.reduce((sum, m) => sum + (Number(m.concessionalUsed) || 0), 0);
}

/**
 * Derive household TSB for Division 296 from SMSF state.
 * TSB = SMSF member balances + external super held outside the fund (all funds combined).
 * @param {object|null} smsfState
 * @returns {{ tsbStart: number, tsbEnd: number, smsfBalance: number, externalSuper: number }}
 */
export function deriveDivision296TsbFromSmsfState(smsfState) {
    if (!smsfState) {
        return { tsbStart: 0, tsbEnd: 0, smsfBalance: 0, externalSuper: 0 };
    }

    const members = smsfState.smsfData?.members ?? smsfState.members ?? [];
    const smsfFromMembers = members.reduce((sum, m) => sum + (Number(m.balance) || 0), 0);
    const externalFromMembers = members.reduce((sum, m) => sum + (Number(m.externalSuper) || 0), 0);

    const smsfBalance = Number(smsfState.smsfData?.balance ?? smsfState.smsfData?.fundBalance ?? 0)
        || smsfFromMembers;
    const externalSuper = Number(smsfState.externalSuperBalance ?? smsfState.totalExternalSuper ?? 0)
        || externalFromMembers;

    const explicitTsb = Number(smsfState.totalSuperBalance ?? smsfState.smsfTotalSuperBalance);
    const tsbEnd = explicitTsb > 0 ? explicitTsb : (smsfBalance + externalSuper);

    const explicitStart = Number(smsfState.tsbStart ?? smsfState.division296?.tsbStart);
    const tsbStart = explicitStart > 0 ? explicitStart : Math.max(0, Math.round(tsbEnd * 0.93));

    return { tsbStart, tsbEnd, smsfBalance, externalSuper };
}

/**
 * Prefill Division 293 concessional contributions from SMSF state.
 * @param {object|null} smsfState
 * @returns {{ concessionalContributions: number }}
 */
export function deriveDivision293DefaultsFromSmsfState(smsfState) {
    const cc = deriveAssessableContributionsFromSmsfState(smsfState);
    return { concessionalContributions: cc > 0 ? cc : undefined };
}
